import ItemInformation from '@/components/general/ItemInforDetail';
import { ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { PET_TYPE, LANGUAGES_SUPPORTED, GENDER } from '@/types/enums';
import { PetGeneralInfo } from '@/types/pet';
import { UserGeneralInfor } from '@/types/user';
import { calculateAge } from '@/utils/datetime';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PetDefault from '@/assets/images/pet-default.png';

export default observer(function PetInformation() {
    // hooks
    const { t } = useTranslation();
    // store
    const { appointmentStore: { appointment } } = useStore();

    return (
        <div className='p-2 bg-white rounded h-full'>
            <h3 className='font-semibold text-active text-[0.9375rem]'>{t('words_title.pet_information')}</h3>
            <div className='mt-4 grid grid-cols-2 gap-x-2 gap-y-3 pb-2 border-b border-b-border-8'>
                <PetOwnerInformation petOwner={appointment?.petOwner} />
            </div>
            <div className='mt-3'>
                <PetDetailInformation petDetail={appointment?.pet} note={appointment?.note} />
            </div>
        </div>
    );
});

function PetOwnerInformation({ petOwner }: { petOwner: UserGeneralInfor }) {
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();
    return (
        <>
            <ItemInformation
                label={t('words_title.pet_owner')}
                value={(
                    <div
                        className={classNames(petOwner?.isDeleted === false && 'cursor-pointer text-link-1 underline')}
                        onClick={() => {
                            if (petOwner?.isDeleted === false) {
                                navigate(`${ROUTES.userManagement.href}/${petOwner?.code}`);
                            }
                        }}
                    >
                        {petOwner?.fullName}
                    </div>
                )}
            />
            <ItemInformation
                label={t('words_title.phone_number')}
                value={petOwner?.phoneNumber}
            />
            <ItemInformation
                label={t('words_title.email')}
                value={petOwner?.email || '--'}
            />
            <ItemInformation
                label={t('words_title.address')}
                value={
                    `${petOwner?.address ? `${petOwner.address}, ` : ''}${petOwner?.ward ? (language === LANGUAGES_SUPPORTED.English ? petOwner?.ward?.fullNameEn : petOwner?.ward?.fullName) + ', ' : ''
                    }${petOwner?.district ? (language === LANGUAGES_SUPPORTED.English ? petOwner?.district?.fullNameEn : petOwner?.district?.fullName) + ', ' : ''
                    }${petOwner?.province ? (language === LANGUAGES_SUPPORTED.English ? petOwner?.province?.fullNameEn : petOwner?.province?.fullName) : ''
                    }`
                }
            />

        </>
    );
}

function PetDetailInformation({ petDetail, note }: { petDetail: PetGeneralInfo, note?: string }) {
    // hook
    const { t, i18n: { language } } = useTranslation();

    return (
        <>
            <div className='flex gap-3'>
                <div className='min-h-32 min-w-32'>
                    <img src={petDetail?.image ? petDetail?.image : PetDefault} className='h-32 w-32 rounded-[0.25rem]' alt='pet img' />
                </div>
                <div className='flex flex-col py-2 gap-2'>
                    <p className='font-medium'>{petDetail?.name}</p>
                    <div className='flex gap-2'>
                        <p className='text-[0.8125rem] text-text-4'>{petDetail?.typeAnimal === PET_TYPE.Dog ? t('select_options.my_pet_dog') : (petDetail?.typeAnimal === PET_TYPE.Cat ? t('select_options.my_pet_cat') : t('select_options.my_pet_other'))}</p>
                        {
                            petDetail?.species && (
                                <>
                                    <div className='h-full w-[0.125rem] flex justify-center items-center'>
                                        <div className='w-[0.125rem] h-[80%] bg-border-8 rounded-md'></div>
                                    </div>
                                    <p className='text-[0.8125rem] text-text-4'>
                                        {typeof petDetail?.species === 'string' ?
                                            petDetail?.species :
                                            (language === LANGUAGES_SUPPORTED.Vietnamese ? petDetail?.species?.name : petDetail?.species?.nameEn)}
                                    </p>
                                </>
                            )
                        }
                    </div>
                    <div className='flex gap-2 items-center'>
                        {
                            petDetail?.gender !== null && (
                                <>
                                    <p className='text-[0.8125rem] text-text-4'>
                                        {
                                            petDetail?.gender === GENDER.Male ? t('words_title.male') : t('words_title.female')
                                        }
                                    </p>
                                    {petDetail?.birthday && (
                                        <div className='w-[0.125rem] h-[70%] bg-border-8 rounded-md'></div>
                                    )}
                                </>
                            )
                        }
                        {
                            petDetail?.birthday && (
                                <p className='text-[0.8125rem] text-text-4'>
                                    {(() => {
                                        const age = calculateAge(petDetail?.birthday ?? '');
                                        const yearLabel = age.years > 0 ? `${age.years} ${age.years !== 1 ? t('words_title.years') : t('words_title.year')}` : '';
                                        const monthLabel = `${age.months} ${age.months !== 1 ? t('words_title.months') : t('words_title.month')}`;
                                        return yearLabel ? `${yearLabel} ${monthLabel}` : monthLabel;
                                    })()}
                                </p>
                            )
                        }
                    </div>
                </div>
            </div>
            <div className='flex flex-col'>
                <label className='mt-2 text-text-4'>{t('words_title.note')}</label>
                <p className='whitespace-pre-line'>{note || '--'}</p>
            </div>
        </>
    );
}
